// @ts-strict-ignore
import { Snackbar } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import { AviaryProvider } from "@aviary/utils";
import { LazyExperimentTester } from "@shared/components/ExperimentTester/LazyExperimentTester/LazyExperimentTester";
import { LoadingProvider } from "@shared/components/LoadingProvider/LoadingProvider";
import { PageViewLogger } from "@shared/components/PageViewLogger/PageViewLogger";
import { PreloadFingerprint } from "@shared/components/PreloadFingerprint";
import { createExperimentVar } from "@shared/context/experiment/experiment.store";
import { FlippersProvider } from "@shared/context/FlippersContext";
import {
  GlobalContextUserRole,
  GlobalContextProvider as SharedGlobalContextProvider,
} from "@shared/context/GlobalConfig";
import type { Flipper } from "@shared/types/flipper";
import { GlobalContextProvider } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { ApolloClientProvider } from "@unauthenticated/signupSignin/components/ApolloClientProvider/ApolloClientProvider";
import { i18n } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  treatmentPlanTerm?: string;
  currentCountry: string;
  flippers: Flipper[];
  isEmerson?: boolean;
  e2e: boolean;
  analyticsAnonymousId: string;
  hydrated?: boolean;
  children: ReactNode;
}

const Providers = ({
  children,
  treatmentPlanTerm,
  currentCountry,
  flippers,
  isEmerson,
  e2e,
  analyticsAnonymousId,
  hydrated = true,
}: Props) => {
  createExperimentVar({ analyticsAnonymousId });

  return (
    <FlippersProvider flippers={flippers}>
      <PreloadFingerprint />
      <LoadingProvider>
        <ApolloClientProvider>
          <I18nextProvider i18n={i18n}>
            <SharedGlobalContextProvider
              country={currentCountry}
              isEmerson={isEmerson}
              role={GlobalContextUserRole.UNAUTHENTICATED}
              e2e={e2e}
            >
              <GlobalContextProvider treatmentPlanTerm={treatmentPlanTerm}>
                <AviaryProvider isEmerson={isEmerson} hydrated={hydrated}>
                  <PageViewLogger isUnauth>{children}</PageViewLogger>
                  <LazyExperimentTester />
                  <Snackbar />
                </AviaryProvider>
              </GlobalContextProvider>
            </SharedGlobalContextProvider>
          </I18nextProvider>
        </ApolloClientProvider>
      </LoadingProvider>
    </FlippersProvider>
  );
};

export { Providers };
